import { SDUIImage } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { SDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import {
  LoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { TopShopIcon } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Avatar/__internal__/TopShopIcon";
import {
  ICON_SIZE_MAP,
  TOP_SHOP_ICON,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Avatar/__internal__/TopShopUtils";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { Core } from "@faire/design-tokens";
import { getColor } from "@faire/sdui/lib/getColor";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { IAvatar } from "@faire/web-api--source/indigofair/data/IAvatar";
import { IAvatarElement } from "@faire/web-api--source/indigofair/data/IAvatarElement";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import {
  AvatarIconPosition,
  AvatarSize,
  AvatarSizeToTypographyVariant,
  DEGREE_BY_POSITION,
  DIMENSION_BY_SIZE,
} from "@faire/web/slate-deprecated/Avatar/utils";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import React from "react";

/**
 * Note: the SDUI Avatar does not have support for responsive picture <source>s that have different DPRs,
 * which means we are not prerendering the correct image sizes on the server.
 * This causes a redownload of the image and gives extended UPLT measurements.
 *
 * This Avatar renderer is thus implemented using SDUIImage, which supports this better.
 */
export const SDUIAvatarElementRenderer: React.FC<{
  className?: string;
  componentStyle?: React.CSSProperties;
  avatarElement: IAvatarElement;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  loggingData: LoggingEventParams | undefined;
}> = ({
  className,
  componentStyle,
  avatarElement,
  renderBehaviour,
  loggingData,
}) => {
  const { avatar, on_click } = avatarElement ?? {};
  const { image_avatar, text_avatar, overlay_badge, style } = avatar ?? {};
  const { size = "S", border } = style ?? {};

  const slateWebAvatarSize = getSlateWebAvatarSize(size);
  const avatarSizePx = `${DIMENSION_BY_SIZE[slateWebAvatarSize]}px`;
  const avatarIconData =
    overlay_badge?.image && overlay_badge.position
      ? {
          position: getSlateWebAvatarIconPosition(overlay_badge.position),
          icon:
            overlay_badge.image.url === TOP_SHOP_ICON ? (
              <TopShopIcon iconSize={ICON_SIZE_MAP[slateWebAvatarSize]} />
            ) : (
              <SDUIImage
                optimizeImageOptions={{
                  width: DIMENSION_BY_SIZE[slateWebAvatarSize] / 2,
                  height: DIMENSION_BY_SIZE[slateWebAvatarSize] / 2,
                }}
                responsiveImage={{ mobileAndAbove: overlay_badge.image }}
                renderBehaviour={renderBehaviour}
              />
            ),
        }
      : undefined;

  if (!text_avatar && !image_avatar) {
    return null;
  }

  // SDUI has a special use case for the avatar element to display a `+N` avatar
  // indicating that there is more than 1 user represented by this avatar
  // However, Slate does not want us modifying the core Slate Avatar with this
  // functionality (see https://faire-wholesale.slack.com/archives/C0155F6D2NL/p1742484129400079)
  // so this is a workaround to display this full text value only in SDUI Avatar
  //
  // Note that this AvatarElement is deprecated and should be repaced by SDUIAvatar
  // soon too, so this should be temporary
  const isPlusNMembersText = /\+\d+/.test(text_avatar ?? "");

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
      className={cn(className, "relative z-10 grid")}
      componentStyle={componentStyle}
    >
      {avatarIconData ? (
        <span
          className={cn(
            "absolute transform-[translate(50%,-50%)]",
            iconTopStyleVar.className,
            iconRightStyleVar.className
          )}
          style={assignInlineVars({
            [iconTopStyleVar.variable]: `${50 * (1 - Math.sin(degreeToRadian(DEGREE_BY_POSITION[avatarIconData.position])))}%`,
            [iconRightStyleVar.variable]: `${50 * (1 - Math.cos(degreeToRadian(DEGREE_BY_POSITION[avatarIconData.position])))}%`,
            [avatarSizeStyleVar.variable]: avatarSizePx,
          })}
        >
          {avatarIconData.icon}
        </span>
      ) : null}
      <span
        className={cn(
          avatarSizeStyleVar.className,
          "col-[1] row-[1] rounded-full",
          image_avatar ? undefined : "bg-core-surface-default",
          getSlateWebAvatarBorderStyle(border?.border_style)
        )}
        style={{
          borderColor: border?.border_color
            ? getColor(
                border?.border_color,
                ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN
              )
            : undefined,
          ...assignInlineVars({
            [avatarSizeStyleVar.variable]: avatarSizePx,
          }),
        }}
      >
        <SDUIImage
          showPlaceholder={false}
          className="block h-full w-full overflow-hidden rounded-full"
          responsiveImage={{ mobileAndAbove: image_avatar ?? IImage.build() }}
          optimizeImageOptions={{
            // always use x-large for maximizing CDN cache hits. This will be greater than the savings of using the actual size
            width: DIMENSION_BY_SIZE["x-large"],
            height: DIMENSION_BY_SIZE["x-large"],
          }}
        />
      </span>
      {text_avatar ? (
        <span className="z-10 col-[1] row-[1] flex items-center justify-center">
          <Typography
            variant={AvatarSizeToTypographyVariant[slateWebAvatarSize]}
            color={Core.text.inverse}
          >
            {isPlusNMembersText
              ? text_avatar?.toUpperCase()
              : text_avatar.at(0)?.toUpperCase()}
          </Typography>
        </span>
      ) : null}
    </SDUIOnClickHandler>
  );
};

// mobile uses S,M,L,etc. for avatar sizing, so the proto value needs to be converted from those to Slate web versions
export const getSlateWebAvatarSize = (
  size: keyof typeof IAvatar.AvatarSize | undefined
): AvatarSize => {
  switch (size) {
    case "XXS":
      return "xx-small";
    case "XS":
      return "x-small";
    case "S":
      return "small";
    case "M":
      return "medium";
    case "L":
      return "large";
    case "XL":
      return "x-large";
    default:
      return "medium";
  }
};

const getSlateWebAvatarIconPosition = (
  position: keyof typeof IAvatar.IOverlayBadge.Position | undefined
): AvatarIconPosition => {
  switch (position) {
    case "TOP_LEFT":
      return "topLeft";
    case "BOTTOM_LEFT":
      return "bottomLeft";
    case "TOP_RIGHT":
      return "topRight";
    case "BOTTOM_RIGHT":
    default:
      return "bottomRight";
  }
};

const getSlateWebAvatarBorderStyle = (
  borderStyle: keyof typeof IAvatar.IAvatarStyle.AvatarBorderStyle | undefined
) => {
  switch (borderStyle) {
    case "SLATE_OUTLINED":
      return "border-solid border-2";
    case "THIN":
      return "border-solid border-[1px]";
    default:
      return undefined;
  }
};

const degreeToRadian = (degrees: number) => degrees * (Math.PI / 180);

const iconTopStyleVar: DynamicVariable<"--f-sdui-avatar-icon-top"> = {
  variable: "--f-sdui-avatar-icon-top",
  className: "top-(--f-sdui-avatar-icon-top)",
};

const iconRightStyleVar: DynamicVariable<"--f-sdui-avatar-icon-right"> = {
  variable: "--f-sdui-avatar-icon-right",
  className: "right-(--f-sdui-avatar-icon-right)",
};

const avatarSizeStyleVar: DynamicVariable<"--f-sdui-avatar-size"> = {
  variable: "--f-sdui-avatar-size",
  className: "w-(--f-sdui-avatar-size) h-(--f-sdui-avatar-size)",
};
